// Use the /min build (Mexico + 11 popular countries, ~80 KB) instead of the
// full library (~155 KB). SpotMe is Mexico-only; full international metadata
// is dead weight. Lighthouse flagged ~110 KB of unused libphonenumber JS on
// pages where the phone validator loads via LeadForm / CaptureLead / KYC.
// /min doesn't ship the legacy `isValidNumber` alias — use `isValidPhoneNumber`
// (identical single-arg behavior).
import {
  AsYouType,
  isValidPhoneNumber as isValidNumber
} from 'libphonenumber-js/min'
import { useCallback, useState } from 'react'

type Options = {
  /** Country dial code without the "+" — defaults to "52" (Mexico). */
  defaultCountryCode?: string
}

type UsePhoneInputReturn = {
  phoneNumber: string
  isValid: boolean
  hasError: boolean
  onChange: (e: { target: { value: string } }) => void
  setPhoneNumber: (value: string) => void
}

/**
 * Smart auto-prefix: turns bare digits into a libphonenumber-friendly
 * E.164-ish input by adding "+" and (if needed) a country code.
 *
 * Behaviour:
 *  - Already starts with "+" → leave alone.
 *  - 11 digits starting with 1 → US/Canada paste, prepend "+".
 *  - Digits already start with the default country code → just prepend "+".
 *  - Otherwise → prepend `+${defaultCountryCode}`.
 *
 * Rationale: SpotMe is Mexico-first, so unprefixed input defaults to +52,
 * and the helper/error copy on PhoneField surfaces +1 explicitly so US
 * users know to type the leading "+1".
 */
export function applyAutoPrefix(
  raw: string,
  defaultCountryCode = '52'
): string {
  if (!raw) return ''
  if (raw[0] === '+') return raw
  const digits = raw.replace(/\D/g, '')
  if (!digits) return raw
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits
  if (digits.startsWith(defaultCountryCode)) return '+' + digits
  return `+${defaultCountryCode}${digits}`
}

/**
 * Format a raw phone string for display: applies the auto-prefix above
 * and runs libphonenumber-js's AsYouType formatter for spacing.
 */
export function formatPhoneInput(
  raw: string,
  defaultCountryCode = '52'
): string {
  return new AsYouType().input(applyAutoPrefix(raw, defaultCountryCode))
}

/**
 * Centralised phone input hook for uncontrolled / non-RHF cases.
 *
 * For RHF forms, prefer <PhoneField> (src/components/Inputs/PhoneField).
 *
 * Usage:
 *   const phone = usePhoneInput(initialValue)
 *   <BaseTextField value={phone.phoneNumber} onChange={phone.onChange} error={phone.hasError} />
 *   {phone.hasError && <p>{tCommon('FORMS.INVALID_PHONE_HINT')}</p>}
 *   if (!phone.isValid) return // block submit
 */
export default function usePhoneInput(
  initialValue = '',
  options: Options = {}
): UsePhoneInputReturn {
  const { defaultCountryCode = '52' } = options
  const [phoneNumber, setPhoneNumber] = useState(initialValue)

  const isValid = !phoneNumber || isValidNumber(phoneNumber)
  const hasError = !!phoneNumber && !isValid

  const onChange = useCallback(
    (e: { target: { value: string } }) => {
      setPhoneNumber(formatPhoneInput(e.target.value, defaultCountryCode))
    },
    [defaultCountryCode]
  )

  return { phoneNumber, isValid, hasError, onChange, setPhoneNumber }
}

/**
 * react-hook-form `validate` rule. Returns `true` for empty values so the
 * caller can pair this with `required` to control empty-vs-invalid behaviour.
 */
export const validatePhoneRule = (value: string | undefined) => {
  if (!value) return true
  return isValidNumber(value) || 'FORMS.INVALID_PHONE_HINT'
}
